import { useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { FULL_SCREEN_STATE_MIN_H } from '@/lib/constants/layout';
import { ROUTES } from '@/lib/constants/routes';
import { useDataRoomLaunch } from '../hooks/useDataRoomLaunch';
import { toRenderableRefusal } from '../api/data-room.api';
import { DATA_ROOM_RETURN_PARAMS, DATA_ROOM_STRINGS } from '../constants';
import { DataRoomRefusal } from './DataRoomRefusal';

interface HandoffLocationState {
  startupId?: string;
}

/**
 * The handoff interstitial (MYN-164): a brief "opening your data room" state
 * that launches on arrival and then hands the browser to the server-rendered
 * form that POSTs to DAXE.
 *
 * It renders no token and no fragment of one - it never receives one. The
 * handoff itself replaces this history entry, so it is not a route the user
 * can navigate back into.
 *
 * ## It is reached two ways
 *
 * 1. **From the launch affordance**, which since UX round 3 opens this page in
 *    a new tab with the acting startup in the query string (router state does
 *    not cross tabs); location state is still read for any in-tab caller.
 * 2. **As the return from a refused handoff**, which is a fresh top-level
 *    navigation carrying `outcome`, `reason` and - when the Platform knew it -
 *    `startupId` in the query string. Router state is gone by then, which is
 *    why the startup is read from the URL as well.
 *
 * `outcome=reissue` relaunches and enters NO error state: a 60-second
 * single-use ticket expiring on a tab the member left is a mechanism failure,
 * and the HANDLE row requires the data room to simply open. `outcome=error`
 * renders that reason's state instead of launching, so a refusal is never
 * followed by another doomed attempt.
 *
 * A reissue with no startup in the URL cannot relaunch anything - only DAXE's
 * own return is missing it - so it falls through to the reason's state, which
 * for a mechanism label is the retryable "temporarily unavailable" one.
 */
export function DataRoomHandoff() {
  const [searchParams] = useSearchParams();
  const stateStartupId = (useLocation().state as HandoffLocationState | null)?.startupId ?? null;
  const returnedStartupId = searchParams.get(DATA_ROOM_RETURN_PARAMS.STARTUP_ID);
  const startupId = stateStartupId ?? returnedStartupId;

  const outcome = searchParams.get(DATA_ROOM_RETURN_PARAMS.OUTCOME);
  const returnedReason = searchParams.get(DATA_ROOM_RETURN_PARAMS.REASON);
  // A returned error is rendered as-is; a returned reissue is only actionable
  // while a startup is known, and otherwise renders its reason too.
  const refusedOnReturn =
    outcome === 'error' || (outcome === 'reissue' && !startupId)
      ? toRenderableRefusal(returnedReason)
      : null;

  const navigate = useNavigate();
  const { launch, isLaunching, refusal } = useDataRoomLaunch(startupId ?? '');
  const hasLaunched = useRef(false);

  // Only a known startup can be relaunched. Without one - DAXE's own return
  // names none - "try again" goes back to the dashboard, which is where the
  // launch affordance lives, rather than firing a request with no startup in
  // its path.
  const retry = startupId ? launch : () => navigate(ROUTES.DASHBOARD, { replace: true });

  useEffect(() => {
    if (!startupId || refusedOnReturn || hasLaunched.current) return;
    hasLaunched.current = true;
    launch();
  }, [startupId, refusedOnReturn, launch]);

  if (refusedOnReturn) return <DataRoomRefusal reason={refusedOnReturn} onRetry={retry} />;

  if (!startupId) return <Navigate to={ROUTES.DASHBOARD} replace />;

  if (refusal) return <DataRoomRefusal reason={refusal} onRetry={launch} />;

  return (
    <div className={`flex items-center justify-center px-4 py-16 ${FULL_SCREEN_STATE_MIN_H}`}>
      <div className="flex flex-col items-center gap-6 text-center" aria-busy={isLaunching}>
        <LoadingSpinner size="lg" label={DATA_ROOM_STRINGS.HANDOFF.TITLE} />
        <div className="flex flex-col gap-2">
          <h1 className="text-h3 text-navy font-sans">{DATA_ROOM_STRINGS.HANDOFF.TITLE}</h1>
          <p className="text-subtext text-sm">{DATA_ROOM_STRINGS.HANDOFF.BODY}</p>
        </div>
      </div>
    </div>
  );
}
